import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { uploadImage } from '../utils/imageUpload';

const AddBannerDialog = ({ open, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    image_en: '',
    image_fin: '',
    image_fr: '',
    image_de: '',
    alt_text_en: '',
    alt_text_fin: '',
    alt_text_fr: '',
    alt_text_de: '',
    rank: 1,
    status: 1
  });

  const [selectedFiles, setSelectedFiles] = useState({
    en: null,
    fin: null,
    fr: null,
    de: null
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fin', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (lang, event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [lang]: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [`image_${lang}`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.image_en && !formData.image_fin && !formData.image_fr && !formData.image_de) {
      setError('At least one image is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First, upload images if any files are selected
      const uploadedUrls = {};
      
      for (const lang of ['en', 'fin', 'fr', 'de']) {
        if (selectedFiles[lang]) {
          try {
            const uploadedUrl = await uploadImage(selectedFiles[lang]);
            uploadedUrls[`image_${lang}`] = uploadedUrl;
          } catch (uploadError) {
            setError(`Failed to upload ${lang.toUpperCase()} image`);
            setLoading(false);
            return;
          }
        } else {
          uploadedUrls[`image_${lang}`] = formData[`image_${lang}`] || '';
        }
      }

      const response = await fetch('https://elnaf.fi/new/api/create_banner.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_en: uploadedUrls.image_en || '',
          image_fin: uploadedUrls.image_fin || '',
          image_fr: uploadedUrls.image_fr || '',
          image_de: uploadedUrls.image_de || '',
          alt_text_en: formData.alt_text_en || '',
          alt_text_fin: formData.alt_text_fin || '',
          alt_text_fr: formData.alt_text_fr || '',
          alt_text_de: formData.alt_text_de || '',
          rank: parseInt(formData.rank),
          status: formData.status
        })
      });

      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      console.log('Data received:', data);
      console.log('onSave function:', onSave);
      
      // Check for success - backend might return different messages
      if (response.ok && (data.status === 'success' || data.message?.includes('uğurla'))) {
        setSuccess(true);
        console.log('Success detected! Will close in 1.5 seconds');
        // Show success for 1.5 seconds then close
        setTimeout(() => {
          console.log('Timeout reached, calling onSave');
          if (onSave) {
            console.log('Calling onSave now');
            onSave(); // This will refresh and close
          } else {
            console.log('onSave is undefined!');
          }
        }, 1500);
      } else {
        setError(data.message || 'Failed to create banner');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      image_en: '',
      image_fin: '',
      image_fr: '',
      image_de: '',
      alt_text_en: '',
      alt_text_fin: '',
      alt_text_fr: '',
      alt_text_de: '',
      rank: 1,
      status: 1
    });
    setSelectedFiles({
      en: null,
      fin: null,
      fr: null,
      de: null
    });
    setError('');
    setSuccess(false);
    setActiveTab(0);
  };

  const handleClose = () => {
    if (!loading && !success) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Banner</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Banner created successfully!
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rank (Order)"
                name="rank"
                type="number"
                value={formData.rank}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 1}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      status: e.target.checked ? 1 : 0
                    }))}
                  />
                }
                label={formData.status === 1 ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            {languages.map((lang, index) => (
              <Tab key={lang.code} label={lang.label} />
            ))}
          </Tabs>

          {languages.map((lang, index) => (
            <Box
              key={lang.code}
              hidden={activeTab !== index}
              sx={{ pt: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Upload {lang.label} Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileSelect(lang.code, e)}
                    />
                  </Button>
                  {selectedFiles[lang.code] && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Selected: {selectedFiles[lang.code].name}
                    </Typography>
                  )}
                  {formData[`image_${lang.code}`] && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img
                        src={formData[`image_${lang.code}`]}
                        alt={`Preview ${lang.label}`}
                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={`Alt Text (${lang.label})`}
                    name={`alt_text_${lang.code}`}
                    value={formData[`alt_text_${lang.code}`]}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Created!' : 'Create Banner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBannerDialog;